import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AnalysisResults from '../../components/AnalysisResults';
import EssayForm from '../../components/TextAnalyzer/EssayForm';
import { SpellCheckMatch } from '@/services/spellCheckerService';
import { spellCheckerService } from '@/services/spellCheckerService';
import './AnalysisResultsPage.css';

const AnalysisResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const  { data, analysis } = location.state || {};

  console.log({analysis, data})


  const [highlightedWord, setHighlightedWord] = useState<{ offset: number; length: number } | null>(null);
  
  // Spell check states - exactly like TextAnalyzer
  const [spellCheckResults, setSpellCheckResults] = useState<SpellCheckMatch[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [ignoredErrors, setIgnoredErrors] = useState<Set<string>>(new Set());
  const [ essayText, setEssayText ] = useState(data?.essay || '');
  const [currentAnalysis, setCurrentAnalysis] = useState(analysis);
  const [currentData, setCurrentData] = useState(data);



  useEffect(() => {
    const runSpellCheck = async () => {
      if (!essayText.trim()) {
        setSpellCheckResults([]);
        return;
      }
      try {
        const response = await spellCheckerService.checkSpelling(essayText);
        setSpellCheckResults(response.matches);
      } catch (error) {
        console.error('Spell check error:', error);
        setSpellCheckResults([]);
      } finally {
        setIsChecking(false);
      }
    };
    runSpellCheck();
  }, [essayText]);

  const handleWordHighlight = useCallback((offset: number, length: number) => {
    setHighlightedWord({ offset, length });
  }, []);

  const handleSuggestionReplace = useCallback(async (match: SpellCheckMatch, suggestion: string) => {
    const { offset, length } = match;
    const beforeText = essayText.substring(0, offset);
    const afterText = essayText.substring(offset + length);
    const newText = beforeText + suggestion + afterText;
    
    // Clear highlighted word since offsets will change
    setHighlightedWord(null);
    setEssayText(newText);
    
    // Trigger fresh spell check after a short delay to allow text update
    setTimeout(async () => {
      if (newText.trim()) {
        setIsChecking(true);
        try {
          const response = await spellCheckerService.checkSpelling(newText);
          setSpellCheckResults(response.matches);
        } catch (error) {
          console.error('Spell check error after replacement:', error);
          setSpellCheckResults([]);
        } finally {
          setIsChecking(false);
        }
      }
    }, 100);
  }, [essayText]);

  const handleIgnoreError = (match: SpellCheckMatch) => {
    setIgnoredErrors(prev => new Set([...prev, `${match.offset}-${match.length}`]));
  };

  const filteredSpellCheckResults = spellCheckResults.filter(
    result => !ignoredErrors.has(`${result.offset}-${result.length}`)
  );

  const handleBackToEditor = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAnalysisUpdate = useCallback((analysis: any, updatedData: any) => {
    setCurrentAnalysis(analysis);
    setCurrentData(updatedData);
  }, []);

    if (!analysis) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>No Analysis Data</h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>Please analyze an essay first.</p>
          <button
            onClick={handleBackToEditor}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Editor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row px-4 gap-6 h-full overflow-y-auto">
      <div className="w-full lg:w-1/2 p-4 rounded-lg overflow-y-auto max-h-[530px]">
        <EssayForm
          highlightedWord={highlightedWord}
          spellCheckResults={filteredSpellCheckResults}
          essayText={essayText}
          setEssayText={setEssayText}
          data={currentData}
          onAnalysisUpdate={handleAnalysisUpdate}
        />
      </div>

      <div className="w-full lg:w-[70%] rounded-lg p-4 overflow-y-auto max-h-[520px]">
        <AnalysisResults 
          analysis={currentAnalysis} 
          onWordHighlight={handleWordHighlight}
          onSuggestionReplace={handleSuggestionReplace}
          spellCheckResults={filteredSpellCheckResults}
          isChecking={isChecking}
          onIgnoreError={handleIgnoreError}
        />
      </div>
    </div>
  );
};

export default AnalysisResultsPage; 